/* eslint-disable @typescript-eslint/no-empty-function */
import React, { lazy } from 'react';
import connect from '@src/js/separate-logic/connect';
import { RegionClassName } from '@src/js/constant';
import styles from './index.less';
import ContainerPresenter from './container-presenter';
import ContainerModel from './container-model';
import LazyBoundary from '@com/LazyBoundary';
import Canvas from '../components/canvas';
import LeftBar from '@com-pc/left-bar';
import EditingPresentFollowBtn from '@src/framework/frame/components/editing-present-follow-btn';
import StatusBar from '@com-pc/status-bar';
import '../../../css/common/ppt-editor-global.css';
import { UPLOAD_PICTURE_OCR_ID } from '../../../js/constants';
import { scheduleAfterFirstSlideDisplay, scheduleAfterFirstScreenDisplay } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IImportFileManager } from '@src/slide/application/service/import-file-manager/core/import-file-manager.interface';
import AsyncBoundary from '@src/framework/frame/components/async-boundary';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { IHistoryManager } from '@src/slide/application/service/history-manager/core/history-manager.interface';
import { IEditingPresentManager } from '@src/slide/application/service/editing-present-manager/core/editing-present-manager.interface';
import { IPcCanvasService } from '@src/slide/application/service/canvas-component/core/pc/type';
import { ILiveDropdownService } from '@src/slide/application/service/live-dropdown-service/core/interface';
import { IImageUploadService } from '@src/slide/application/service/image-upload-service/core/interface';
import classNames from 'classnames';

interface IWrapperProps {
    // model props
    showStatusBar: boolean;
    // parent props
    style: React.CSSProperties;
    historyManager?: IHistoryManager;
    editingPresentManager?: IEditingPresentManager;
    importFileManager?: IImportFileManager;
    getImportFileManager: () => IDynamicService<IImportFileManager> | undefined;
    getCanvasService: () => IPcCanvasService;
    getLiveDropdownDynamicService: () => IDynamicService<ILiveDropdownService> | undefined;
    getImageUploadDynamicService: () => IDynamicService<IImageUploadService> | undefined;
}

const PagingSelection = lazy(() => scheduleAfterFirstScreenDisplay(() => import(/* webpackChunkName: "lazyComponents" */ '@com-pc/paging-selection')));
const ImageUpload = lazy(() => scheduleAfterFirstScreenDisplay(() => import(/* webpackChunkName: "lazyComponents" */ '@frame/components/property-editor/editor-components/image-upload')));
const LiveDropdown = lazy(() => scheduleAfterFirstSlideDisplay(() => import(/* webpackChunkName: "lazyComponents" */ '@com-pc/live-dropdown')));

class Container extends React.Component<IWrapperProps> {
    public static defaultProps = {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        setRef: () => {},
    };

    public shouldComponentUpdate(nextProps: Readonly<IWrapperProps>): boolean {
        return nextProps.style.display !== this.props.style.display;
    }

    public render() {
        const { showStatusBar } = this.props;
        const wrapperStyles = { ...this.props.style };

        return (
            <div id={RegionClassName.PPT_EDITOR} className={`${styles.home} header ${RegionClassName.PPT_EDITOR}`} style={wrapperStyles}>
                <LazyBoundary fallback={<div style={{ height: 0 }}/>} renderAfterPromise={
                    this.props.getLiveDropdownDynamicService()?.promise()
                }>
                    {/* 远程演示下拉菜单 */}
                    <LiveDropdown getLiveDropdownService={this.getLiveDropdownService} />
                </LazyBoundary>
                <div
                    className={classNames({
                        [styles.container]: true,
                        [styles['container-with-status']]: showStatusBar,
                        context: true,
                    })}
                >
                    <Canvas canvasService={this.props.getCanvasService()} />
                    <LeftBar />
                    {/** drawer占位，解决drawer滚动事件传递到canvas-wrapper的问题 */}
                    { <div id='slide-drawer-holder' className={styles.drawer}></div>}
                    {this.props.historyManager?.renderHistoryLoadingMask()}
                    {/* 帮助中心，由于层级问题放在这里处理，放在document下会导致播放态也会展示帮助中心 */}
                    {showStatusBar ? (
                        <div className="fake-status-bar">
                            <div className="fake-left-bar"></div>
                            <div className="fake-canvas"></div>
                        </div>
                    ) : (
                        <></>
                    )}
                </div>

                {this.props.editingPresentManager && (
                    <EditingPresentFollowBtn
                        editingPresentManager={this.props.editingPresentManager}
                    />
                )}

                {showStatusBar ? <StatusBar /> : null}
                {/* 想要添加到body上的控件可以放这里 */}
                <div id="panel-body" style={{ position: 'absolute' }}/>
                <div id="import-file-container"/>
                <input id={UPLOAD_PICTURE_OCR_ID} type="file" accept="image/*" hidden/>

                {/* 分页上传缩略图组件 */}
                <LazyBoundary>
                    <PagingSelection />
                </LazyBoundary>

                {/* 分页上传组件 */}
                <AsyncBoundary>
                    {async () => this.props.getImportFileManager()?.promise()
                        .then(importFileManager => importFileManager.getLazyImportFileComponent())}
                </AsyncBoundary>

                {/* 图片上传组件 */}
                <LazyBoundary renderAfterPromise={
                    this.props.getImageUploadDynamicService()?.promise()
                }>
                    {
                        <ImageUpload getImageUploadService={this.getImageUploadService}/>
                    }
                </LazyBoundary>

                {this.props.historyManager?.renderSnapshotRecovery()}
            </div>
        );
    }

    private getLiveDropdownService = () => this.props.getLiveDropdownDynamicService()?.dangerousInstance;
    private getImageUploadService = () => this.props.getImageUploadDynamicService()?.dangerousInstance;
}

export default connect(ContainerPresenter, ContainerModel, Container);

export const testObj = {
    Container,
};
